import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'

import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card'
import { Table, TableBody, TableCell, TableRow } from '@/components/ui/table'
import { KpiUserAssignmentsModel } from '@/types'
import { calculateBonusPercentage, calculatePayoutAmount } from '@/utils'

interface PayoutSummaryProps {
  employeeId: string
  assignmentsData: KpiUserAssignmentsModel[]
}

const EMPLOYEE_IDS_ELIGIBLE_BONUSES = [
  // ege
  {
    id: '6',
    maxEligibleBonusPercentage: 40,
  },
  // bora
  {
    id: '10',
    maxEligibleBonusPercentage: 30,
  },
  // mert
  {
    id: '9',
    maxEligibleBonusPercentage: 35,
  },
]

const PayoutSummary = ({ employeeId, assignmentsData }: PayoutSummaryProps) => {
  const { t } = useTranslation()

  const [employeeSalary] = useState(18.5)
  const [employeeHrsPerWeek] = useState(40)
  const [weeks] = useState(12)
  const [eligibleBonusPercentage, setEligibleBonusPercentage] = useState(40)
  const [totalBonusPercentage, setTotalBonusPercentage] = useState(0)

  const [eligibleBonusAmount, setEligibleBonusAmount] = useState(0)
  const [payoutAmount, setPayoutAmount] = useState(0)

  useEffect(() => {
    const achievedBonusPercentage = calculateBonusPercentage(assignmentsData)
    setTotalBonusPercentage(achievedBonusPercentage)

    // FIXME: get max eligible bonus percentage from backend
    const maxEligibleBonusPercentage =
      EMPLOYEE_IDS_ELIGIBLE_BONUSES.find(
        (employee) => employee.id === employeeId
      )?.maxEligibleBonusPercentage || 40 // FIXME: 40 as default now
    setEligibleBonusPercentage(maxEligibleBonusPercentage)

    // calculate at backend
    const { eligibleBonusAmount, payoutAmount } = calculatePayoutAmount(
      employeeSalary,
      employeeHrsPerWeek,
      weeks,
      achievedBonusPercentage,
      maxEligibleBonusPercentage
    )
    setEligibleBonusAmount(eligibleBonusAmount)
    setPayoutAmount(payoutAmount)
  }, [employeeSalary, employeeHrsPerWeek, weeks, assignmentsData])

  const summaryData = [
    {
      label: t('targets.payoutSummary.salary'),
      value: `$${employeeSalary.toFixed(2)}`,
    },
    {
      label: t('targets.payoutSummary.hrsPerWeek'),
      value: employeeHrsPerWeek.toString(),
    },
    {
      label: t('targets.payoutSummary.weeks'),
      value: '12',
    },
    {
      label: t('targets.payoutSummary.maxEligibleBonusPercentage'),
      value: `${eligibleBonusPercentage}%`,
    },
    {
      label: t('targets.payoutSummary.achievedBonusPercentage'),
      value: `${totalBonusPercentage}%`,
    },
  ]

  return (
    <div className="mt-8">
      <Card>
        <CardHeader>
          <CardTitle>{t('targets.payoutSummary.title')}</CardTitle>
          <CardDescription>
            {t('targets.payoutSummary.description')}
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex flex-col gap-4 max-w-md">
            <Card>
              <CardContent className="p-0">
                <Table>
                  <TableBody>
                    {summaryData.map((item, index) => (
                      <TableRow
                        key={item.label}
                        className={
                          index < summaryData.length - 1 ? 'border-b' : ''
                        }
                      >
                        <TableCell className="font-medium">
                          {item.label}
                        </TableCell>
                        <TableCell className="text-right">
                          {item.value}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="p-0">
                <Table>
                  <TableBody>
                    <TableRow className="font-semibold">
                      <TableCell>
                        {t('targets.payoutSummary.eligibleBonusAmount')}
                      </TableCell>
                      <TableCell className="text-right">
                        ${eligibleBonusAmount.toFixed(2)}
                      </TableCell>
                    </TableRow>
                    <TableRow className="font-semibold">
                      <TableCell>{t('targets.payoutSummary.payout')}</TableCell>
                      <TableCell className="text-right">
                        ${payoutAmount.toFixed(2)}
                      </TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          </div>
        </CardContent>
      </Card>
    </div>
  )
}

export default PayoutSummary
